import * as stylex from "@stylexjs/stylex";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { press } from "./press";
export type MonthGridDay = {
  key: string;
  date: number;
  name: string;
  marked: boolean;
};
type MonthGridProps = {
  days: readonly MonthGridDay[];
  leading: number;
  weekdays: readonly string[];
  selected: string;
  today: string;
  cell: number;
  onSelect: (day: string) => void;
};
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
const GAP = 6;
const DATE_RATIO = 0.34;
const DOT_RATIO = 0.14;
const _temp = {
  k1xSpc: "xrvj5dj",
  kumcoG: "xqketvx",
  kOIVth: "x17d4w8g",
  kMwMTN: "x1a11ngh",
  "$$css": true
};
const _temp2 = {
  kBCFzs: "x14d52f8",
  "$$css": true
};
const _temp3 = {
  kVAEAm: "x1n2onr6",
  kmVPX3: "x1717udv",
  kMzoRj: "xc342km",
  kaIpWk: "xqhrb7l",
  kWkggS: "xjbqb8w",
  kMwMTN: "x1heor9g",
  kMv6JI: "x17jlq2w",
  k63SB2: "x1v94iar",
  kcqcaj: "xss6m8b",
  kkrTdU: "x1ypdohk",
  k1ekBW: "x18dpk69",
  kIyJzY: "xlc9y7w",
  kAMwcw: "x1em5f5i",
  "$$css": true
};
const _temp4 = {
  kVAEAm: "x10l6tqk",
  kbCHJM: "x1nrll8i",
  kzqmXN: "x1v4s8kt",
  kZKoxP: "xols6we",
  koQZXg: "x1xr3403",
  kaIpWk: "xqhrb7l",
  kWkggS: "x7ns719",
  "$$css": true
};
const styles = {
  grid: (cell: number) => [_temp, {
    "--x-gridTemplateColumns": `repeat(7, ${cell}px)` != null ? `repeat(7, ${cell}px)` : undefined
  }],
  blank: (columns: number) => [_temp2, {
    "--x-gridColumn": `span ${columns}` != null ? `span ${columns}` : undefined
  }],
  day: (cell: number) => [_temp3, {
    kzqmXN: cell != null ? "x5lhr3w" : cell,
    kZKoxP: cell != null ? "x16ye13r" : cell,
    kGuDYH: Math.round(cell * DATE_RATIO) != null ? "xdmh292" : Math.round(cell * DATE_RATIO),
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(cell),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(cell),
    "--x-fontSize": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(Math.round(cell * DATE_RATIO))
  }],
  selected: {
    kWkggS: "xfenp3h",
    kMwMTN: "xnnvkky",
    k63SB2: "xfbjfs5",
    $$css: true
  },
  today: {
    kGVxlE: "xuamy0h",
    k63SB2: "xfbjfs5",
    $$css: true
  },
  dot: (cell: number) => [_temp4, {
    krVfgx: Math.round(cell * DOT_RATIO) != null ? "x1nqzi6q" : Math.round(cell * DOT_RATIO),
    $$css: true
  }, {
    "--x-bottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(Math.round(cell * DOT_RATIO))
  }],
  dotOnSelected: {
    kWkggS: "x1f7iotj",
    $$css: true
  }
};
export function MonthGrid({
  days,
  leading,
  weekdays,
  selected,
  today,
  cell,
  onSelect
}: MonthGridProps) {
  return <div {...stylex.props(styles.grid(cell))}>
      {WEEKDAY_KEYS.map((key, index) => <span key={key} aria-hidden="true" className="x1vvogim x2b8uid xr0iwhn xhunfcg xt3et7w x13npcv9">
          {weekdays[index]}
        </span>)}
      {leading > 0 && <span {...stylex.props(styles.blank(leading))} />}
      {days.map(day => <GridDay key={day.key} day={day} cell={cell} selected={day.key === selected} today={day.key === today} onClick={() => onSelect(day.key)} />)}
    </div>;
}
type GridDayProps = {
  day: MonthGridDay;
  cell: number;
  selected: boolean;
  today: boolean;
  onClick: () => void;
};
function GridDay({
  day,
  cell,
  selected,
  today,
  onClick
}: GridDayProps) {
  return <button type="button" aria-label={day.name} aria-pressed={selected} aria-current={today ? "date" : undefined} onClick={onClick} {...stylex.props(styles.day(cell), today && !selected && styles.today, selected && styles.selected, press.scale)}>
      {day.date}
      {day.marked && <span data-marked="" aria-hidden="true" {...stylex.props(styles.dot(cell), selected && styles.dotOnSelected)} />}
    </button>;
}